import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "Hooks/reduxHook";
import { SnackBarContext } from "Context/SnackContext";
import { Header } from "Components/Header";
import { Customer } from "Features/services/customer.api";
import { getAccessToken } from "Features/slice/customer/customer.Slice";
import { Colors } from "Utils/colors";
import { LocalImage } from "Utils/images";
import moment from "moment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ReactPaginate from "react-paginate";
import "./PaginationStyling.css";
import { Invoices } from "Features/services/invoice.api";
import InvoiceDetailModal from "./InvoiceDetailModal";
import { revertStatus } from "../../Utils/statusFormat";
import { useNavigate } from "react-router-dom";

interface Invoice {
  docNumber: string;
  invoiceId: string;
  customer: string;
  billAmount: number;
  createdAt: string;
  invoiceStatus: string;
  status: string;
}

const InvoiceDetails = () => {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { type } = location.state || {};

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('');

  const handleClick = (event:any, invoiceId:string) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoiceId(invoiceId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedInvoiceId('');
  };

  const { access_token } = useAppSelector((state: any) => state.customer);
  const { handleSnackbar } = useContext(SnackBarContext);

  const [trackStatusUpdated, setTrackStatusUpdated] = useState<boolean>(false)

  const [rows, setRows] = useState<Invoice[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [invoiceDetail, setInvoiceDetail] = useState<any>();

  const [notes, setNotes] = useState<any>([])
  const [invoiceId, setInvoiceId] = useState<any>("")
  const [trackDeleteNotes, setTrackDeleteNotes] = useState<boolean>(false)

  const [invoiceModal, setInvoiceModal] = useState<boolean>(false);

  const openInvoiceModal = () => {
    setInvoiceModal(true);
  };

  const closeInvoiceModal = () => {
    setInvoiceModal(false);
  };

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;
  const startPosition = (currentPage - 1) * itemsPerPage + 1;

  const handlePageClick = (event: { selected: number }) => {
    setCurrentPage(event.selected + 1);
  };

  useEffect(() => {
    dispatch(getAccessToken());
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let response;

        const payload = {
          access_token,
          maxResults: itemsPerPage,
          startPosition,
        };
        
        switch (type) {
          case "recentlyCompleted":
            response = await Invoices.instance.recentInvoices(payload);
            break;
          case "pendingPayment":
            response = await Invoices.instance.pendingPaymentInvoices(payload);
            break;
            default:
              console.error("Unknown type");
              return;
            }
            
        const result = response?.data?.data || [];
        setTotalCount(response?.data?.totalCount);
        setRows(result);
      } catch (error: any) {
        console.error("Error fetching data", error);
        handleSnackbar(true, `${error.message}`, "error");
      } finally {
        setLoading(false);
      }
    };

    if (type && access_token) {
      fetchData();
    }
  }, [type, access_token, currentPage, trackStatusUpdated]);

  const handleDownloadPDF = async (invoiceId: string) => {
    const payload = {
      access_token,
      invoiceId,
    };

    try {
      handleSnackbar(true, "File is being downloaded...");

      const response = await Invoices.instance.downloadInvoice(payload);

      // Log response data
      const reader = new FileReader();
      reader.onload = () => {
        console.log("PDF Content:", reader.result);
      };
      reader.readAsText(response.data);

      // Create a Blob from the response data
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });

      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(pdfBlob);

      // Create a temporary <a> element to trigger the download
      const tempLink = document.createElement("a");
      tempLink.href = url;
      tempLink.setAttribute("download", "invoice.pdf");

      // Append the <a> element to the body and click it to trigger the download
      document.body.appendChild(tempLink);
      tempLink.click();

      // Clean up the temporary elements and URL
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);

      handleSnackbar(true, "Successfully Downloaded");
    } catch (error: any) {
      handleSnackbar(true, error?.message || "An error occurred");
      console.error("Error downloading PDF:", error);
    }
  };

  const handleViewInvoice = async (invoiceId: string) => {

    const payload = {
      access_token,
      invoiceId,
    };

    try {
      setLoading(true);
      const response = await Invoices.instance.invoiceDetail(payload);
      if (response?.data) {
        setInvoiceDetail(response.data);
        setInvoiceModal(true);
      }
    } catch (error: any) {
      handleSnackbar(true, error?.message || "An error occurred");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewNotes = async (invoiceId: string) => {
    try {

      const notesResponse = await Invoices.instance.getAllNotes(invoiceId);
      setNotes(notesResponse.data.data);
    } catch (error: any) {
      handleSnackbar(true, error?.message || "An error occurred");
      console.error("Error:", error);
    }
  }

  useEffect(() => {
    if (invoiceId) {
      handleViewNotes(invoiceId);
    }
  }, [trackDeleteNotes]);
  

  const getDisplayedEntries = () => {
    if (totalCount === 0) return "0";
    const endPosition = Math.min(startPosition + itemsPerPage - 1, totalCount);
    return endPosition;
  };

  const getDisplayLabel = (type: any) => {
    switch (type) {
      case "recentlyCompleted":
        return "Recently Completed";
      case "pendingPayment":
        return "Pending Payment";
      default:
        return "Unknown Type";
    }
  };

  const handleDownloadCSV = async (selectedInvoiceId: string) => {
    const payload = {
      access_token,
      invoiceId: selectedInvoiceId,
    };
  
    try {
      handleSnackbar(true, "File is being downloaded...");
      const response = await Invoices.instance.downloadInvoiceCSV(payload);
      
      // Create a URL for the blob object
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
  
      // Create a link element and set its href to the blob URL
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'invoice.csv');
  
      // Append the link to the body and trigger the download
      document.body.appendChild(link);
      link.click();
  
      // Clean up by removing the link and revoking the object URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
  
      handleSnackbar(true, "CSV File Successfully Downloaded");
    } catch (error: any) {
      handleSnackbar(true, error?.message || "An error occurred");
      console.error("Error downloading CSV:", error);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        backgroundImage: `url(${LocalImage.contactBg})`,
        backgroundRepeat: "no-repeat",
        backgroundColor: `${Colors.white}`,
        backgroundSize: "20% auto",
        backgroundPosition: "right bottom",
        flex: 1,
        overflow: "hidden",
        pl: 3,
        pr: 2,
        pt: 15,
        pb: 8,
      }}
    >
      <Header
        title={getDisplayLabel(type)}
        subTitle="You doing good, Keep it up!"
        btnName="New Invoice"
        onInputChange={() => {}}
        onButtonClick={() => navigate("/invoice/createInvoice")}
      />

      <Box sx={{ width: "100%" }} className="table-top-border"></Box>

      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2, boxShadow: "none" }}>
          <TableContainer sx={{ maxHeight: "60vh", overflowY: "auto" }}>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#e6f1f5" }}>
                    Invoice ID
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#e6f1f5" }}>
                    Customer
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#e6f1f5" }}>
                    Bill Amount
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#e6f1f5" }}>
                    Document Number
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#e6f1f5" }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#e6f1f5" }}>
                    Created At
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: "#e6f1f5", textAlign: "center" }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          fontWeight: "bolder",
                          height: "70vh",
                        }}
                      >
                        <CircularProgress />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {rows.map((row, index) => {
                      const labelId = `enhanced-table-checkbox-${index}`;
                      return (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={row.invoiceId}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            align="left"
                          >
                            {row.invoiceId}
                          </TableCell>
                          <TableCell align="left">{row.customer}</TableCell>
                          <TableCell align="left">${row.billAmount}</TableCell>
                          <TableCell align="left">{row.docNumber}</TableCell>
                          <TableCell align="left">
                            {revertStatus(row?.invoiceStatus)}
                          </TableCell>
                          <TableCell align="left">
                            {moment(row.createdAt).format(
                              "h:mm a, Do MMMM YYYY"
                            )}
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: "15px",
                            }}
                          >
                            <IconButton
                              size="small"
                              style={{ color: "orange", cursor: "pointer" }}
                              onClick={() => {
                                setInvoiceId(row.invoiceId);
                                handleViewInvoice(row.invoiceId);
                                handleViewNotes(row.invoiceId);
                              }
                              }
                              aria-label="view"
                            >
                              View
                            </IconButton>

                            <IconButton
                              color="primary"
                              aria-label="download"
                              onClick={(e) => handleClick(e, row.invoiceId)}
                            >
                              <DownloadIcon
                                sx={{
                                  "&:hover": {
                                    border: "3px solid #1976d2",
                                    borderRadius: "50%",
                                    p: "1px",
                                  },
                                  
                                }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Menu for Download Options */}
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                    >
                      <MenuItem
                        onClick={() => {
                          handleDownloadPDF(selectedInvoiceId);
                          handleClose();
                        }}
                      >
                        Download PDF
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleDownloadCSV(selectedInvoiceId);
                          handleClose();
                        }}
                      >
                        Download CSV
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalCount > 1 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 3,
                px: 2,
              }}
            >
              <Typography
                variant="body1"
                component={"body"}
                color={"#717579"}
                fontSize={"14px"}
              >
                Showing {getDisplayedEntries()} from {totalCount} entries
              </Typography>

              <ReactPaginate
                previousLabel={<ArrowBackIosIcon sx={{ fontSize: "14px" }} />}
                nextLabel={<ArrowForwardIosIcon sx={{ fontSize: "14px" }} />}
                pageCount={Math.ceil(totalCount / itemsPerPage)}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                activeClassName="active"
                forcePage={currentPage - 1}
                marginPagesDisplayed={0}
                pageRangeDisplayed={0}
              />
            </Box>
          ) : null}
        </Paper>
      </Box>

      <InvoiceDetailModal
        invoiceModal={invoiceModal}
        closeInvoiceModal={closeInvoiceModal}
        invoiceDetail={invoiceDetail}
        notes={notes}
        setTrackStatusUpdated={setTrackStatusUpdated}
        setTrackDeleteNotes={setTrackDeleteNotes}
      />
    </Box>
  );
};

export default InvoiceDetails;
